/**
 * PreviewService — produces the large image shown in the review pane.
 *
 * Separate from ThumbnailService because it runs at a different moment, a
 * different size, and against a different queue: previews are made just ahead of
 * the cursor, thumbnails are made for everything.
 */

import { cacheKey } from '../domain/cache-key';
import { forDom } from '../adapters/bytes';
import type { BlobStore } from '../adapters/opfs-store';
import type { ImageProcessor } from '../adapters/image-processor';
import type { MediaRepository } from '../repositories';
import type { MediaItem } from '../types';
import type { ChangeSink, DirectoryResolver } from './ports';

const PREVIEW_QUALITY = 0.85;

/** Below this, generating a preview costs more than decoding the original. */
export const PREVIEW_WORTH_IT_BYTES = 900_000;

export class PreviewService {
  /**
   * Generations already under way, keyed by photograph and size.
   *
   * The preview window keeps a band of frames around the cursor, and moving the
   * cursor starts a new pass before the old one has finished — so the same
   * frame is routinely asked for twice at once. Left alone, both passes miss the
   * cache, both decode the original, and both write the same key; whichever one
   * read its blob from the cache is then pointing at a file the other is
   * truncating, and the review pane shows a broken photograph.
   *
   * Sharing one promise makes the second caller wait for the first. The two get
   * the same Blob, and an object URL each — revoking one leaves the other
   * working, because the blob outlives any single URL onto it.
   */
  private readonly inFlight = new Map<string, Promise<Blob | null>>();

  constructor(
    private readonly media: MediaRepository,
    private readonly cache: BlobStore,
    private readonly images: ImageProcessor,
    private readonly resolve: DirectoryResolver,
    private readonly sink: ChangeSink,
  ) {}

  /**
   * A blob ready to display, plus whether it came from the cache.
   * Small files skip the preview pipeline and are handed over as they are.
   */
  blobFor(item: MediaItem, targetPx: number): Promise<Blob | null> {
    const id = `${item.id}:${targetPx}`;
    const existing = this.inFlight.get(id);
    if (existing) return existing;

    const work = this.buildFor(item, targetPx).finally(() => this.inFlight.delete(id));
    this.inFlight.set(id, work);
    return work;
  }

  private async buildFor(item: MediaItem, targetPx: number): Promise<Blob | null> {
    if (item.sourceId === null || item.type !== 'image') return null;

    const directory = await this.resolve(item.sourceId);
    if (!directory) return null;

    if ((item.sizeBytes ?? 0) < PREVIEW_WORTH_IT_BYTES) {
      try {
        return await (await directory.file(item.relPath)).blob();
      } catch {
        return null;
      }
    }

    const key = cacheKey({
      sourceId: item.sourceId,
      relPath: item.relPath,
      sizeBytes: item.sizeBytes,
      modifiedAt: item.modifiedAt,
      kind: 'preview',
      targetPx,
    });

    const cached = await this.cache.getBlob(key);
    if (cached) return cached;

    try {
      const file = await directory.file(item.relPath);
      const resized = await this.images.resize(await file.blob(), targetPx, PREVIEW_QUALITY);

      await this.cache.put(key, resized.bytes);
      this.media.setPreviewKey(item.id, key);
      this.sink.touch();

      return new Blob([forDom(resized.bytes)], { type: 'image/jpeg' });
    } catch {
      return null;
    }
  }
}
